// 📄 components/StepInvoiceParams.tsx
import React from 'react';
import { AlertCircle } from 'lucide-react';
import { InvoiceFormData } from '../../../../../services/commerce/integrationService';

interface StepInvoiceParamsProps {
  invoiceEnabled: boolean;
  invoiceParkNoAcc: boolean;
  invHdrPrkNosesPogrxEnb: boolean;
  onChange: (field: keyof InvoiceFormData, value: any) => void;
}

export function StepInvoiceParams({ invoiceEnabled, invoiceParkNoAcc, invHdrPrkNosesPogrxEnb, onChange }: StepInvoiceParamsProps) {
  return (
    <div className="space-y-4 animate-fadeIn">
      <div className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-3 mb-2 space-y-1">
        <span className="text-[10px] font-bold text-blue-400 uppercase tracking-widest block">Ruta SPRO / Transacción SAP</span>
        <p className="font-mono text-xs text-gray-300">
          <span className="bg-zinc-800 px-1.5 py-0.5 rounded text-gray-400 font-bold mr-1.5">SPRO</span>
          <span>Integration with Other SAP Components → SAP Ariba Cloud Integration Gateway → Application-Specific Settings → Invoice → Maintain Parameters</span>
        </p>
      </div>

      <div>
        <h3 className="text-lg font-medium text-blue-400">1. Maintain Parameters (Habilitar Facturas)</h3>
        <p className="text-sm text-gray-400">
          Habilita la recepción de facturas desde SAP Ariba y define el comportamiento de contabilización (parking) de facturas sin sesión de servicio ni referencia a la OC.
        </p>
      </div>

      <div className="bg-white/5 p-4 rounded-xl border border-white/5 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <label className="block text-sm font-medium text-white">Parámetro <code className="bg-black/30 px-1 rounded font-mono text-blue-400">INVOICE_ENABLED</code></label>
            <p className="text-xs text-gray-400 mt-0.5">Valor bajo (Low) requerido: <code className="bg-black/30 px-1 rounded font-mono">X</code></p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
            <input type="checkbox" checked={invoiceEnabled || false} onChange={(e) => onChange('invoiceEnabled', e.target.checked)} className="sr-only peer" />
            <div className="w-9 h-5 bg-zinc-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-blue-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
          </label>
        </div>

        <div className="border-t border-white/5 pt-3 flex items-center justify-between gap-4">
          <div>
            <label className="block text-sm font-medium text-white">Parámetro <code className="bg-black/30 px-1 rounded font-mono text-blue-400">INVOICE_PARK_NO_ACC</code></label>
            <p className="text-xs text-gray-400 mt-0.5">Parquear factura si no hay asignación de cuenta (Account Assignment)</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
            <input type="checkbox" checked={invoiceParkNoAcc || false} onChange={(e) => onChange('invoiceParkNoAcc', e.target.checked)} className="sr-only peer" />
            <div className="w-9 h-5 bg-zinc-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-blue-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
          </label>
        </div>

        <div className="border-t border-white/5 pt-3 flex items-center justify-between gap-4">
          <div>
            <label className="block text-sm font-medium text-white">Parámetro <code className="bg-black/30 px-1 rounded font-mono text-blue-400">INV_HDR_PRK_NOSES_POGRX_ENB</code></label>
            <p className="text-xs text-gray-400 mt-0.5">Parquear cabecera si no hay HES ni ítem de OC referenciado</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
            <input type="checkbox" checked={invHdrPrkNosesPogrxEnb || false} onChange={(e) => onChange('invHdrPrkNosesPogrxEnb', e.target.checked)} className="sr-only peer" />
            <div className="w-9 h-5 bg-zinc-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-blue-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
          </label>
        </div>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-blue-400 flex gap-3 text-sm">
        <AlertCircle className="w-5 h-5 flex-shrink-0" />
        <div>
          <p className="font-semibold">Comportamiento de Parking</p>
          <p className="text-xs text-blue-400/80 mt-0.5">
            Estos parámetros evitan el rechazo automático de facturas incompletas — en su lugar, las deja parqueadas (MIR7) para revisión manual del equipo de Cuentas por Pagar.
          </p>
        </div>
      </div>
    </div>
  );
}
